import { IconButton } from "@hortis/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@hortis/ui/command";
import {
  CommandInput,
  CommandMobileHeader,
} from "@hortis/ui/command/command-menu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { XClose } from "@hortis/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { ObservationConditionType } from "generated/gql/graphql";
import { HTMLAttributes, type ReactNode, useState } from "react";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { ObservationConditionRating } from "src/features/material-page/sections/material-record-observations/observation-condition";

export const ObservationConditionPicker = ({
  value,
  onChange,
  placeholder: _placeholder,
  children,
  "data-cy": dataCy,
  blockFocus = false,
}: {
  value: ObservationConditionType | undefined | null;
  onChange: (val: ObservationConditionType | undefined | null) => void;
  "data-cy"?: string;
  children: ReactNode;
  placeholder?: string;
  blockFocus?: boolean;
}) => {
  const isDesktop = useMediaQuery("md");
  const placeholder = _placeholder ?? "Select a condition...";
  const [open, setOpen] = useState(false);

  const handleChange = (condition: ObservationConditionType) => {
    onChange(condition);
    setOpen(false);
  };

  const commandContent = (
    <>
      <CommandEmpty>No results</CommandEmpty>
      <CommandList className="px-0 py-0">
        <div className="divide-y divide-grey-200">
          <CommandGroup className="p-1">
            <CommandItem
              onSelect={() => {
                handleChange(ObservationConditionType.Good);
              }}
              data-cy="observation-condition-picker-item"
              selected={value === ObservationConditionType.Good}
            >
              <ObservationConditionRating
                condition={ObservationConditionType.Good}
              />
              Good
            </CommandItem>
            <CommandItem
              onSelect={() => {
                handleChange(ObservationConditionType.Fair);
              }}
              data-cy="observation-condition-picker-item"
              selected={value === ObservationConditionType.Fair}
            >
              <ObservationConditionRating
                condition={ObservationConditionType.Fair}
              />
              Fair
            </CommandItem>
            <CommandItem
              onSelect={() => {
                handleChange(ObservationConditionType.Poor);
              }}
              data-cy="observation-condition-picker-item"
              selected={value === ObservationConditionType.Poor}
            >
              <ObservationConditionRating
                condition={ObservationConditionType.Poor}
              />
              Poor
            </CommandItem>
            <CommandItem
              onSelect={() => {
                handleChange(ObservationConditionType.Dead);
              }}
              data-cy="observation-condition-picker-item"
              selected={value === ObservationConditionType.Dead}
            >
              <ObservationConditionRating
                condition={ObservationConditionType.Dead}
              />
              Dead
            </CommandItem>
          </CommandGroup>
        </div>
      </CommandList>
    </>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          data-cy={dataCy ?? "observation-condition-picker"}
          asChild
          onFocusCapture={stopEventPropagation}
        >
          {children}
        </PopoverTrigger>
        <PopoverContent
          className="z-99999 min-w-(--radix-popover-trigger-width)"
          onCloseAutoFocus={(e) => {
            if (blockFocus) {
              e.stopPropagation();
              e.preventDefault();
            }
          }}
        >
          <Command>{commandContent}</Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        data-cy={dataCy ?? "observation-condition-picker"}
        asChild
        onFocusCapture={stopEventPropagation}
      >
        {children}
      </DialogTrigger>
      <DialogContent className="p-0!">
        <Command size="lg">
          <CommandMobileHeader>
            {placeholder}
            <DialogClose asChild>
              <IconButton
                icon={XClose}
                size="xs"
                ariaLabel="Close"
                variant="tertiaryGray"
              />
            </DialogClose>
          </CommandMobileHeader>
          {commandContent}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export const ObservationConditionMultiPicker = ({
  value,
  onChange,
  placeholder: _placeholder,
  children,
  ...props
}: {
  value: Array<ObservationConditionType> | undefined | null;
  onChange: (val: Array<ObservationConditionType> | undefined | null) => void;
  placeholder?: string;
} & HTMLAttributes<HTMLButtonElement>) => {
  const placeholder = _placeholder ?? "Select a condition...";
  const [open, setOpen] = useState(false);

  const handleChange = (condition: ObservationConditionType) => {
    onChange(
      value?.includes(condition) === true
        ? value.filter((v) => v !== condition)
        : [...(value ?? []), condition],
    );
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        data-cy={"observation-condition-picker"}
        asChild
        onFocusCapture={stopEventPropagation}
        {...props}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent className="z-99999 w-(--radix-popover-trigger-width)!">
        <Command>
          <CommandInput
            placeholder={placeholder}
            data-cy="observation-condition-picker-input"
          />
          <CommandEmpty>No results</CommandEmpty>
          <CommandList>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  handleChange(ObservationConditionType.Good);
                }}
                data-cy="observation-condition-picker-item"
                selected={value?.includes(ObservationConditionType.Good)}
              >
                <ObservationConditionRating
                  condition={ObservationConditionType.Good}
                />
                Good
              </CommandItem>
              <CommandItem
                onSelect={() => {
                  handleChange(ObservationConditionType.Fair);
                }}
                data-cy="observation-condition-picker-item"
                selected={value?.includes(ObservationConditionType.Fair)}
              >
                <ObservationConditionRating
                  condition={ObservationConditionType.Fair}
                />
                Fair
              </CommandItem>
              <CommandItem
                onSelect={() => {
                  handleChange(ObservationConditionType.Poor);
                }}
                data-cy="observation-condition-picker-item"
                selected={value?.includes(ObservationConditionType.Poor)}
              >
                <ObservationConditionRating
                  condition={ObservationConditionType.Poor}
                />
                Poor
              </CommandItem>
              <CommandItem
                onSelect={() => {
                  handleChange(ObservationConditionType.Dead);
                }}
                data-cy="observation-condition-picker-item"
                selected={value?.includes(ObservationConditionType.Dead)}
              >
                <ObservationConditionRating
                  condition={ObservationConditionType.Dead}
                />
                Dead
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
